import { z } from 'zod';

/**
 * One capability the live model can use. Client components render on the Canvas; server tools run behind
 * POST /api/tools/:name. Declarations sent to Gemini are derived from these specs, so the `description` is
 * prompt text: write it for the model, not for humans.
 */
export type ToolSpec<P extends z.ZodObject = z.ZodObject> = {
  name: string;
  description: string;
  params: P;
  execution: 'client' | 'server';
  // BLOCKING: the model waits for our response before speaking. Client renders answer instantly, so they stay
  // BLOCKING. Slow server tools use NON_BLOCKING so the conversation keeps flowing (verified on gemini-3.8-live).
  behavior: 'BLOCKING' | 'NON_BLOCKING';
  // How a NON_BLOCKING result is delivered: SILENT for bookkeeping the model shouldn't talk about (remember),
  // WHEN_IDLE for results it should react to once it finishes speaking. Default WHEN_IDLE.
  responseScheduling?: 'SILENT' | 'WHEN_IDLE' | 'INTERRUPT';
  fixtures: Array<{ name: string; params: z.input<P> }>;
};

export function defineTool<P extends z.ZodObject>(spec: ToolSpec<P>): ToolSpec<P> {
  return spec;
}

export type FunctionDeclaration = {
  name: string;
  description: string;
  parametersJsonSchema: Record<string, unknown>;
  behavior?: 'NON_BLOCKING';
};

export function toFunctionDeclaration(spec: ToolSpec): FunctionDeclaration {
  const schema = z.toJSONSchema(spec.params, { target: 'openapi-3.0' }) as Record<string, unknown>;
  delete schema.$schema;
  return {
    name: spec.name,
    description: spec.description,
    parametersJsonSchema: schema,
    ...(spec.behavior === 'NON_BLOCKING' ? { behavior: 'NON_BLOCKING' as const } : {}),
  };
}
